/**
 * Empty state component for provider progress section.
 * Shows loading, error, or empty states when no events are available.
 */
import type { FC } from "react";

import type { HydrationStatus } from "../../../hooks/useProviderProgress";

import { Alert } from "../../common/Alert";
import { Button } from "../../common/Button";
import { EmptyState } from "../../common/EmptyState";
import { LoadingState } from "../../common/LoadingState";
import { SectionHeader } from "../../common/SectionHeader";

const ProgressContent: FC<{
  hydrationStatus: HydrationStatus;
  isSheetOpen: boolean;
  onRetry: () => void;
}> = ({ hydrationStatus, isSheetOpen, onRetry }) => {
  if (
    hydrationStatus === "loading" ||
    (hydrationStatus === "idle" && isSheetOpen)
  ) {
    return (
      <LoadingState message="Loading provider progress..." className="py-4" />
    );
  }

  if (hydrationStatus === "error") {
    return (
      <Alert
        variant="error"
        action={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="border-rose-400/50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/60 dark:text-rose-200 dark:hover:bg-rose-500/20"
          >
            Retry
          </Button>
        }
      >
        Failed to load provider progress events.
      </Alert>
    );
  }

  return (
    <EmptyState
      title="No provider events yet"
      description="Historical provider progress events will appear here once available."
      size="sm"
      className="!py-6"
    />
  );
};

interface ProviderProgressEmptyStateProps {
  hydrationStatus: HydrationStatus;
  isSheetOpen: boolean;
  taskId: string;
  onRetry: () => void;
}

export const ProviderProgressEmptyState: FC<
  ProviderProgressEmptyStateProps
> = ({ hydrationStatus, isSheetOpen, onRetry }) => (
  <section>
    <SectionHeader
      title="Provider Progress"
      variant="label"
      icon={
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      }
    />
    <div className="rounded-none border border-slate-300 bg-white/70 p-4 shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/40 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
      <ProgressContent
        hydrationStatus={hydrationStatus}
        isSheetOpen={isSheetOpen}
        onRetry={onRetry}
      />
    </div>
  </section>
);
